import { ECustomerEvents, ESessionEvents, TDealerTool, TDgaGenerationLocation } from 'data/analytics';
import { z } from 'zod';
import { State } from 'utils/state';
import { IPublicSession, userSession } from './session';
import { sendCustomerAnalyticsEvent, sendSessionAnalyticsEvent } from 'utils/ui/analytics';
import { user } from 'utils/auth/user';

export const analyticsSchema = z.object({
  voipUsed: z.boolean().optional(),
  dealerToolUsed: z.union([z.custom<TDealerTool>(), z.literal('')]).prefault(''),
  dgaGeneratedAt: z.union([z.custom<TDgaGenerationLocation>(), z.literal('')]).prefault(''),
  isShared: z.boolean().prefault(false),
  experience2DUsed: z.boolean().prefault(false),
  experience3DUsed: z.boolean().prefault(false),
  comparisonUsed: z.boolean().prefault(false),
});

export type IAnalyticsDto = z.infer<typeof analyticsSchema>;

let state: State<IAnalyticsDto>;

export function analyticsState() {
  if (!state) {
    state = new State<IAnalyticsDto>(analyticsInitializationDefaults());
  }
  return state;
}

function analyticsInitializationDefaults() {
  return analyticsSchema.parse({
    voipUsed: false,
    dealerToolUsed: '',
    isShared: false,
    experience2DUsed: false,
    experience3DUsed: false,
  });
}

analyticsState().on('change', () => {
  updateAnalytics();
});

userSession().on('session:start', () => {
  if (!userSession().data.isShared) {
    // If the session is not shared, it means it was just created and we want to log the creation event
    sendSessionAnalyticsEvent(ESessionEvents.SESSION_CREATED, userSession().data);
  }
});

userSession().on('session:shared', () => {
  analyticsState().setValues({ isShared: true }, { emit: false }); // emit: false will prevent the change event from firing and sending another beacon
  sendSessionAnalyticsEvent(ESessionEvents.SESSION_STARTED, userSession().data);
});

userSession().on('change:analytics', (updatedValue?: Partial<IPublicSession>) => {
  if (updatedValue?.sharingType) {
    // Updating the sharing type means the session was started, which is handled in the session:shared event
    return;
  }

  updateAnalytics();
});

// Track 2D/3D experience usage
userSession().on('change', (changedKeys, changedValues, oldValues, newValues, session) => {
  if (changedKeys.includes('activeExperience') && session?.activeExperience && session?.id) {
    const currentAnalytics = analyticsState().get();
    const analyticsFlag = `experience${session.activeExperience}Used` as 'experience2DUsed' | 'experience3DUsed';

    if (!currentAnalytics[analyticsFlag]) {
      analyticsState().setValues({ [analyticsFlag]: true });
    }
  }

  // flag that comparison has been used
  if (changedKeys.includes('comparisonMode') && session?.comparisonMode !== 'off') {
    const currentAnalytics = analyticsState().get();
    if (!currentAnalytics.comparisonUsed) {
      analyticsState().setValues({ comparisonUsed: true });
    }
  }
});

function updateAnalytics({
  keepAlive,
  data = analyticsState().get() as IAnalyticsDto,
}: {
  keepAlive?: boolean;
  data?: Partial<IAnalyticsDto>;
} = {}) {
  if (user.type === 'customer') {
    sendCustomerAnalyticsEvent(ECustomerEvents.STREAM_UPDATED, userSession().data, data as IAnalyticsDto);
    return;
  }
  sendSessionAnalyticsEvent(ESessionEvents.SESSION_UPDATED, userSession().data, keepAlive, data as IAnalyticsDto);
}
